import { Controller } from "@hotwired/stimulus"

// 端末（ブラウザ）ごとの匿名トークンをクライアント側で冗長保存し、受験済みをソフトに制御する。
// - サーバ発行トークン(tokenValue)を localStorage と IndexedDB にミラー（Cookieが消えても残る）。
// - Cookieが消えていても、保存済みトークンを hidden(mirror) に載せてPOSTで送り、サーバが復元・照合する。
// - 受験完了(completedValue)時は完了フラグと結果ページのトークンを保存し、
//   再訪時は前回の結果ページへ誘導する（Cookieが消えていてもクライアント側で遷移できる）。
//
// ※ シークレットモード/全ストレージ削除/別端末では回避可能なソフトな制御（過剰アクセス対策）。
export default class extends Controller {
  static targets = ["mirror", "blockedOverlay"]
  static values = {
    token: String,        // サーバ発行の端末トークン（new画面）
    completed: Boolean,   // 受験完了ページで true
    resultToken: String   // 完了セッションのトークン（結果ページで渡す）
  }

  async connect() {
    // 結果ページ: 完了フラグと結果トークンを保存して終了（次回以降クライアント側でも結果へ誘導）。
    if (this.completedValue) {
      await this.setItem("rp_completed", "1")
      if (this.resultTokenValue) await this.setItem("rp_result_token", this.resultTokenValue)
      return
    }

    // new画面: トークンを確定してミラー。Cookieが消えていた場合の復元に備え hidden へ載せる。
    // 保存済みトークン(stored)を最優先にする（サーバはCookie喪失時に新トークンを発行するため、
    // this.tokenValue を優先すると既存端末の復元・未完了セッションの再開が壊れる）。
    const stored = await this.getItem("rp_device")
    const token = stored || this.tokenValue
    if (token) {
      await this.setItem("rp_device", token)
      if (this.hasMirrorTarget) this.mirrorTarget.value = token
    }

    // 既に受験済みなら、前回の結果ページへ誘導する（サーバ往復を待たない）。
    // 結果トークンが分かる場合は結果ページへ遷移し、分からない古い完了のみオーバーレイで案内する。
    if (await this.getItem("rp_completed")) {
      const resultToken = await this.getItem("rp_result_token")
      if (resultToken) {
        window.location.replace(`/roleplay/${encodeURIComponent(resultToken)}/result`)
      } else {
        this.showBlocked()
      }
    }
  }

  showBlocked() {
    if (this.hasBlockedOverlayTarget) this.blockedOverlayTarget.classList.remove("hidden")
  }

  // --- ストレージ（localStorage + IndexedDB の二重化。片方が使えなくても動く） ---

  async getItem(key) {
    return this.localGet(key) || (await this.idbGet(key))
  }

  async setItem(key, value) {
    this.localSet(key, value)
    await this.idbSet(key, value)
  }

  localGet(key) {
    try { return window.localStorage.getItem(key) } catch { return null }
  }

  localSet(key, value) {
    try { window.localStorage.setItem(key, value) } catch { /* private mode 等では無視 */ }
  }

  // --- IndexedDB（最小のkvストア） ---

  openDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) { resolve(null); return }
      const request = window.indexedDB.open("roleplay", 1)
      request.onupgradeneeded = () => request.result.createObjectStore("kv")
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
    })
  }

  async idbGet(key) {
    try {
      const db = await this.openDb()
      if (!db) return null
      return await new Promise((resolve) => {
        const req = db.transaction("kv", "readonly").objectStore("kv").get(key)
        req.onsuccess = () => resolve(req.result || null)
        req.onerror = () => resolve(null)
      })
    } catch { return null }
  }

  async idbSet(key, value) {
    try {
      const db = await this.openDb()
      if (!db) return
      await new Promise((resolve) => {
        const tx = db.transaction("kv", "readwrite")
        tx.objectStore("kv").put(value, key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
      })
    } catch { /* 使用不可でも localStorage 側でカバー */ }
  }
}
